/* eslint unused-imports/no-unused-imports: "off" */
import React, {useState, useEffect, useRef} from "react"

import {breakpoints} from "@next/themes"
import {TextModel} from "models/Text"
import {Breadcrumbs} from "models/SearchState"
import {checkIfMerchandisedUrl} from "@next/utils"
import {getMoreLessButtonId} from "../../getMoreLessButtonId"
import useStickyObserver from "../../../../hooks/useStickyObserver"
import SingleFacet from "../../../horizontalFilters/facetListBox/singleFacet"
import useSetFiltersOpenFromCookie from "../../../../hooks/useSetFiltersOpenFromCookie"
import HFClearAll from "../../../hfClearAll"
import HFMoreLessButton from "../../../hfMoreLessButton"
import {
    HFStyledHeader,
    StyledNavList,
    StyledMoreLessItem,
    StyledNav,
    FeatNav,
    StyledNavItem,
    StyledNavItemSecondRow,
    StyledNavListSecondRow,
    HFStyledClearAllContainer,
    HFStyledClearAll,
} from "./components"
import {HFStyledIcon} from "../../components"
import env from "../../../../config/env"
import {HORIZONTAL_FILTERS_ITEM_COUNT_DEFAULT, HORIZONTAL_FILTERS_ITEM_COUNT_LARGE} from "../../../../config/constants"
import FacetListBox from "../../../horizontalFilters/facetListBox"
import {FacetState} from "../../../../models/FacetsState"
import useHandleScroll from "../../useHandleScroll"
import {httpUrlTrimmer} from "../../../../utils/httpUrlTrimmer"
import DesktopSort from "../../../desktopSort"
import {calculateSecondaryFilterRows} from "../../calculateSecondaryFilterRows"
import useMediaQuery from "../../../../hooks/useMediaQuery"

export interface FiltersComponentProps {
    consolidatedFilters: string[]
    text: TextModel
    primaryFeatFacets: FacetState[]
    secondaryFeatFacets: FacetState[]
    handleSetFacet: (facetValue: string, filterName: string) => void
    isFilterSelected: boolean
    clearAllFilters: () => void
    url: string
    rememberFilters: boolean
    disableIcon: boolean
    breadcrumbs: Breadcrumbs
    totalRows: number
    datatestId?: string
}

export const normaliseFilters = (
    consolidatedFilters: string[],
    primaryFeatFacets: FacetState[],
    filtersCount: number,
) => {
    const consolidatedFiltersWithoutFeat = consolidatedFilters.filter(filter => filter !== "feat")
    const dynamicFiltersCount = filtersCount - primaryFeatFacets.length
    const firstFilterList = consolidatedFiltersWithoutFeat.slice(0, dynamicFiltersCount)
    const secondFilterList = consolidatedFiltersWithoutFeat.slice(dynamicFiltersCount)
    return {firstFilterList, secondFilterList}
}

export const Cashmere25FiltersComponent = ({
    consolidatedFilters,
    text,
    primaryFeatFacets,
    secondaryFeatFacets,
    handleSetFacet,
    isFilterSelected,
    clearAllFilters,
    url,
    rememberFilters,
    disableIcon,
    breadcrumbs,
    totalRows,
    datatestId,
}: FiltersComponentProps) => {
    const inPageFiltersVisible = useMediaQuery(`(max-width:${breakpoints.values.lg}px)`)
    const navRef = useRef<HTMLDivElement>(null)
    useSetFiltersOpenFromCookie(url, rememberFilters)
    const SecondRowPriceText = "Price"
    const HeaderToggleShadowEventName = "cloud-header-toggle-bottom-shadow"

    const [isMoreShown, setMoreShown] = useState(true)
    const [rowsCount, setRowsCount] = useState<number>(totalRows)
    const [filterItemsCount, setFilterItemsCount] = useState<number>(HORIZONTAL_FILTERS_ITEM_COUNT_LARGE)

    useHandleScroll(isMoreShown, setMoreShown)

    useStickyObserver({
        ref: navRef,
        topOffset: 0,
        eventName: HeaderToggleShadowEventName,
    })

    const toggleFiltersList = () => {
        setMoreShown(prevState => !prevState)
    }

    const calculateRows = (): number => {
        let filtersCount = HORIZONTAL_FILTERS_ITEM_COUNT_DEFAULT
        if (typeof window !== "undefined") {
            const width = window.innerWidth
            if (width >= breakpoints.values.xl) {
                filtersCount = HORIZONTAL_FILTERS_ITEM_COUNT_LARGE
            } else if (width >= breakpoints.values.lg) {
                filtersCount = HORIZONTAL_FILTERS_ITEM_COUNT_DEFAULT
            }
        }

        setFilterItemsCount(filtersCount)
        return calculateSecondaryFilterRows(consolidatedFilters, primaryFeatFacets, secondaryFeatFacets, filtersCount)
    }

    useEffect(() => {
        const handleResize = () => {
            setRowsCount(calculateRows())
        }
        handleResize()
        window.addEventListener("resize", handleResize)
        return () => {
            window.removeEventListener("resize", handleResize)
        }
    }, [consolidatedFilters, primaryFeatFacets, secondaryFeatFacets])

    const {firstFilterList, secondFilterList} = normaliseFilters(
        consolidatedFilters,
        primaryFeatFacets,
        filterItemsCount,
    )
    const showPriceAfterSecondaryFeat = secondaryFeatFacets?.length > 0 && secondFilterList.includes(SecondRowPriceText)
    const isMerchandisedUrl = checkIfMerchandisedUrl(httpUrlTrimmer(url))

    const showMoreLessButton = secondFilterList.length > 0 || secondaryFeatFacets.length > 0
    const allTotalRows = rowsCount || (showMoreLessButton ? rowsCount + 1 : rowsCount)

    return (
        <>
            <HFStyledHeader
                data-scrollanchor={!inPageFiltersVisible}
                id="plp-horizontal-filter-bar"
                data-testid={datatestId}
                breadcrumbsEnabled={breadcrumbs.enabled}
                isMerchandisedUrl={isMerchandisedUrl}
                ref={navRef}
            >
                <StyledNav data-testid="plp-filter-cashmere25-template">
                    <StyledNavList data-testid="horizontal-filter-first-row">
                        {primaryFeatFacets.map(featFacet => {
                            return (
                                <FeatNav key={featFacet.n}>
                                    <SingleFacet
                                        facet={featFacet}
                                        filterName="feat"
                                        handleSetFacet={handleSetFacet}
                                        showCount
                                        filterItemsCount
                                        shouldGetFocus={false}
                                        siteUrl=""
                                    />
                                </FeatNav>
                            )
                        })}
                        {firstFilterList.map(item => {
                            return (
                                <StyledNavItem key={item}>
                                    <FacetListBox key={item} filterName={item} facetBoxMinWidth={15} />
                                </StyledNavItem>
                            )
                        })}
                        {showMoreLessButton && (
                            <StyledMoreLessItem
                                onClick={toggleFiltersList}
                                data-testid={getMoreLessButtonId(isMoreShown)}
                                id={getMoreLessButtonId(isMoreShown)}
                            >
                                <HFMoreLessButton>
                                    {isMoreShown
                                        ? text.pages.filters.labels.moreButton
                                        : text.pages.filters.labels.lessButton}
                                </HFMoreLessButton>
                                {!disableIcon && (
                                    <HFStyledIcon data-testid="plp-horizontal-filter-more-less-icon">
                                        <img
                                            src={`${env.REACT_APP_BLOB_STORAGE_PATH}/icons/pdp/brand/next/filter-m-default.svg`}
                                            alt={text.pages.filters.iconAlt.moreIconAlt}
                                        />
                                    </HFStyledIcon>
                                )}
                            </StyledMoreLessItem>
                        )}
                        <DesktopSort />
                    </StyledNavList>
                    {!isMoreShown && (
                        <StyledNavListSecondRow totalRows={allTotalRows} data-testid="horizontal-filter-second-row">
                            {secondFilterList.map(item => {
                                if (showPriceAfterSecondaryFeat && item === SecondRowPriceText) return null
                                return (
                                    <StyledNavItemSecondRow key={item}>
                                        <FacetListBox
                                            key={item}
                                            filterName={item}
                                            isOnSecondRow
                                            facetBoxMinWidth={15}
                                        />
                                    </StyledNavItemSecondRow>
                                )
                            })}
                            {secondaryFeatFacets.map(featFacet => {
                                return (
                                    <FeatNav key={featFacet.n}>
                                        <SingleFacet
                                            facet={featFacet}
                                            filterName="feat"
                                            handleSetFacet={handleSetFacet}
                                            showCount
                                            shouldGetFocus={false}
                                            siteUrl=""
                                        />
                                    </FeatNav>
                                )
                            })}
                            {showPriceAfterSecondaryFeat && (
                                <StyledNavItemSecondRow
                                    data-testid="plp-filter-cashmere25-2nd-row-price"
                                    key={SecondRowPriceText}
                                >
                                    <FacetListBox
                                        key={SecondRowPriceText}
                                        filterName={SecondRowPriceText}
                                        isOnSecondRow
                                        facetBoxMinWidth={15}
                                    />
                                </StyledNavItemSecondRow>
                            )}
                        </StyledNavListSecondRow>
                    )}
                </StyledNav>
            </HFStyledHeader>
            <HFStyledClearAllContainer>
                <HFStyledClearAll>
                    {isFilterSelected && (
                        <HFClearAll childtestid="plp-horizontal-filter-clear-all" clearAllFilters={clearAllFilters}>
                            {text.buttons.clearAllFilters}
                        </HFClearAll>
                    )}
                </HFStyledClearAll>
            </HFStyledClearAllContainer>
        </>
    )
}

export default Cashmere25FiltersComponent
